import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock, Zap, TrendingUp, Crown } from 'lucide-react';
import type { SubscriptionPlan, Feature } from '@/config/plans';
import { PLAN_LABELS, FEATURE_LABELS } from '@/config/plans';

interface UpgradePromptProps {
  feature: Feature;
  minimumPlan: SubscriptionPlan;
  currentPlan?: SubscriptionPlan;
  reason?: string;
  variant?: 'card' | 'modal' | 'inline';
  className?: string;
}

/**
 * Componente para incentivar upgrade de plano
 * Exibido quando usuário tenta acessar funcionalidade bloqueada
 */
export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  feature,
  minimumPlan,
  currentPlan,
  reason,
  variant = 'card',
  className = '',
}) => {
  const getPlanIcon = (plan: SubscriptionPlan) => {
    switch (plan) {
      case 'basic':
        return <Zap className="h-5 w-5" />;
      case 'premium':
        return <TrendingUp className="h-5 w-5" />;
      case 'enterprise':
        return <Crown className="h-5 w-5" />;
      default:
        return <Lock className="h-5 w-5" />;
    }
  };

  const getPlanColor = (plan: SubscriptionPlan) => {
    switch (plan) {
      case 'basic':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'premium':
        return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
      case 'enterprise':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  if (variant === 'inline') {
    return (
      <div className={`flex items-center gap-2 p-3 rounded-lg bg-muted/50 border ${className}`}>
        <Lock className="h-4 w-4 text-muted-foreground" />
        <p className="text-sm text-muted-foreground flex-1">
          {reason || `${FEATURE_LABELS[feature]} está disponível no plano ${PLAN_LABELS[minimumPlan]}`}
        </p>
        <Button size="sm" variant="outline" asChild>
          <a 
            href="https://wa.me/5521968215101?text=Olá!%20Gostaria%20de%20saber%20mais%20sobre%20os%20planos%20do%20Grana%20em%20Dia" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Fazer Upgrade
          </a>
        </Button>
      </div>
    );
  }

  return (
    <Card className={`max-w-md mx-auto ${className}`}>
      <CardHeader>
        <div className="flex items-center gap-3 mb-2">
          <div className={`p-2 rounded-lg ${getPlanColor(minimumPlan)}`}>
            {getPlanIcon(minimumPlan)}
          </div>
          <Badge variant="outline" className={getPlanColor(minimumPlan)}>
            {PLAN_LABELS[minimumPlan]}
          </Badge>
        </div>
        <CardTitle className="text-xl">Funcionalidade Premium</CardTitle>
        <CardDescription>
          {FEATURE_LABELS[feature]} está disponível apenas para assinantes
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {reason && (
          <p className="text-sm text-muted-foreground">
            {reason}
          </p>
        )}
        
        {currentPlan && (
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <span className="text-sm text-muted-foreground">Seu plano atual:</span>
            <Badge variant="secondary">{PLAN_LABELS[currentPlan]}</Badge>
          </div>
        )}

        <div className="space-y-2">
          <h4 className="font-semibold text-sm">Benefícios do upgrade:</h4>
          <ul className="space-y-1.5 text-sm text-muted-foreground">
            {minimumPlan === 'basic' && (
              <>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span>Relatórios financeiros detalhados</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span>Assistente automático no WhatsApp</span>
                </li>
              </>
            )}
            {minimumPlan === 'premium' && (
              <>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span>Todos os recursos do plano Básico</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span>Agenda financeira completa</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span>Assistente com Inteligência Artificial</span>
                </li>
              </>
            )}
            {minimumPlan === 'enterprise' && (
              <>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span>Todos os recursos dos planos anteriores</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span>Agente de Voz com IA avançada</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500 mt-0.5">✓</span>
                  <span>Suporte prioritário</span>
                </li>
              </>
            )}
          </ul>
        </div>
      </CardContent>

      <CardFooter className="flex gap-2">
        <Button className="flex-1" asChild>
          <a 
            href="https://wa.me/5521968215101?text=Olá!%20Gostaria%20de%20saber%20mais%20sobre%20os%20planos%20do%20Grana%20em%20Dia" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Ver Planos
          </a>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/dashboard">
            Voltar
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
};

/**
 * Componente para exibir bloqueio de assinatura inativa
 */
interface SubscriptionBlockedProps {
  status: 'late' | 'canceled';
  message?: string;
  className?: string;
}

export const SubscriptionBlocked: React.FC<SubscriptionBlockedProps> = ({
  status,
  message,
  className = '',
}) => {
  return (
    <Card className={`max-w-md mx-auto border-amber-500/20 ${className}`}>
      <CardHeader>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-amber-500/10">
            <Lock className="h-5 w-5 text-amber-500" />
          </div>
          <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20">
            {status === 'late' ? 'Pagamento Atrasado' : 'Assinatura Cancelada'}
          </Badge>
        </div>
        <CardTitle className="text-xl">
          {status === 'late' ? 'Regularize seu pagamento' : 'Reative sua assinatura'}
        </CardTitle>
        <CardDescription>
          {message || 'Seu acesso está temporariamente limitado'}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {status === 'late' 
            ? 'Para continuar aproveitando todas as funcionalidades, por favor regularize seu pagamento.'
            : 'Reative sua assinatura para voltar a ter acesso completo a todas as funcionalidades.'
          }
        </p>

        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-sm">
            <strong>Funcionalidades básicas</strong> continuam disponíveis:
          </p>
          <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5">✓</span>
              <span>Visão Geral</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5">✓</span>
              <span>Transações</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5">✓</span>
              <span>Metas</span>
            </li>
          </ul>
        </div>
      </CardContent>

      <CardFooter>
        <Button className="w-full" asChild>
          <Link to="/assinatura">
            {status === 'late' ? 'Regularizar Pagamento' : 'Reativar Assinatura'}
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
};
